/**
 * Custom partner map popup (outside Google InfoWindow).
 * Anchored bubble above marker on all viewports.
 */
export class PartnerMapPopup {
    /** Clearance above the pin tip so the bubble sits over the icon, not on it. */
    static PIN_HEIGHT = 38;
    static PIN_GAP = 8;
    static CARET_SIZE = 9;
    static EDGE_PAD = 8;

    static OPEN_CLASSES = [
        'opacity-100',
        'visible',
        'pointer-events-auto',
    ];

    static CLOSED_CLASSES = [
        'opacity-0',
        'invisible',
        'pointer-events-none',
    ];

    constructor(popupEl, { onClose } = {}) {
        this.root = popupEl || null;
        this.panelEl =
            this.root?.querySelector('[js-map-popup-panel]') ?? null;
        this.contentEl =
            this.root?.querySelector('[js-map-popup-content]') ?? null;
        this.closeBtn =
            this.root?.querySelector('[js-map-popup-close]') ?? null;
        this.onClose = typeof onClose === 'function' ? onClose : null;
        this.onCloseClick = this.onCloseClick.bind(this);
        this.onResize = this.onResize.bind(this);
        this.isOpen = false;
        this.map = null;
        this.position = null;
        this.overlay = null;

        if (this.closeBtn) {
            this.closeBtn.addEventListener('click', this.onCloseClick);
        }

        window.addEventListener('resize', this.onResize);
    }

    get available() {
        return Boolean(this.root && this.contentEl);
    }

    setMap(map) {
        this.map = map || null;
        if (this.map) {
            this.ensureOverlay();
        }
    }

    ensureOverlay() {
        if (this.overlay || !this.map || typeof google === 'undefined') {
            return;
        }

        const popup = this;

        class PopupOverlay extends google.maps.OverlayView {
            onAdd() {}

            draw() {
                popup.updatePosition();
            }

            onRemove() {}
        }

        this.overlay = new PopupOverlay();
    }

    onCloseClick(e) {
        e.preventDefault();
        e.stopPropagation();
        if (this.onClose) {
            this.onClose();
        } else {
            this.close();
        }
    }

    onResize() {
        if (!this.isOpen) return;
        this.updatePosition();
    }

    async open(data, position = null) {
        if (!this.available) return;

        this.contentEl.innerHTML = this.render(data);
        this.position = position || this.position;
        this.root.hidden = false;
        this.root.setAttribute('aria-hidden', 'false');
        this.root.setAttribute('data-open', 'true');
        this.isOpen = true;

        this.root.classList.add('is-open');
        this.root.classList.remove(...PartnerMapPopup.CLOSED_CLASSES);
        this.root.classList.add(...PartnerMapPopup.OPEN_CLASSES);

        if (this.map && this.overlay) {
            this.overlay.setMap(this.map);
        }

        await this.waitForProjection();
        this.updatePosition();

        requestAnimationFrame(() => {
            this.updatePosition();
        });
    }

    waitForProjection() {
        return new Promise(resolve => {
            let tries = 0;
            const tick = () => {
                if (this.overlay?.getProjection?.() || tries >= 30) {
                    resolve();
                    return;
                }
                tries += 1;
                requestAnimationFrame(tick);
            };

            requestAnimationFrame(tick);
        });
    }

    clearPositionStyles() {
        if (!this.root) return;
        this.root.style.left = '';
        this.root.style.top = '';
        this.root.style.transform = '';
        this.root.style.right = '';
        this.root.style.bottom = '';
        this.root.style.width = '';
    }

    updatePosition() {
        if (!this.isOpen || !this.root || !this.position) {
            return;
        }

        const projection = this.overlay?.getProjection?.();
        if (!projection) return;

        const latLng =
            this.position instanceof google.maps.LatLng
                ? this.position
                : new google.maps.LatLng(this.position);

        const point = projection.fromLatLngToContainerPixel(latLng);
        if (!point) return;

        const mapRect = this.map?.getDiv?.()?.getBoundingClientRect?.();
        const shell = this.root.offsetParent;
        const shellRect = shell?.getBoundingClientRect?.();

        let x = point.x;
        let y = point.y;

        if (mapRect && shellRect) {
            x += mapRect.left - shellRect.left;
            y += mapRect.top - shellRect.top;
        }

        const popupWidth = this.root.offsetWidth || 320;
        const shellWidth = shell?.clientWidth || mapRect?.width || popupWidth;
        const pad = PartnerMapPopup.EDGE_PAD;
        const caret = PartnerMapPopup.CARET_SIZE;
        // LatLng is the pin tip; icon rises upward — keep bubble above the whole pin.
        const pinClearance =
            PartnerMapPopup.PIN_HEIGHT + PartnerMapPopup.PIN_GAP;

        const caretTipY = y - pinClearance;

        let left = x;
        const halfW = popupWidth / 2;
        left = Math.min(
            Math.max(left, halfW + pad),
            shellWidth - halfW - pad
        );

        // Root bottom = caret base; caret tip is `caret` px below that.
        this.root.style.left = `${left}px`;
        this.root.style.top = `${caretTipY - caret}px`;
        this.root.style.transform = 'translate(-50%, -100%)';
    }

    close() {
        if (!this.root) return;

        this.isOpen = false;
        this.overlay?.setMap(null);
        this.root.classList.remove('is-open');
        this.root.classList.add(...PartnerMapPopup.CLOSED_CLASSES);
        this.root.classList.remove(...PartnerMapPopup.OPEN_CLASSES);
        this.root.setAttribute('aria-hidden', 'true');
        this.root.setAttribute('data-open', 'false');
        this.root.hidden = true;
        this.clearPositionStyles();
    }

    destroy() {
        window.removeEventListener('resize', this.onResize);

        if (this.closeBtn) {
            this.closeBtn.removeEventListener('click', this.onCloseClick);
        }

        this.overlay?.setMap(null);
        this.overlay = null;

        if (this.root) {
            this.root.classList.remove('is-open');
            this.root.classList.add(...PartnerMapPopup.CLOSED_CLASSES);
            this.root.classList.remove(...PartnerMapPopup.OPEN_CLASSES);
            this.root.hidden = true;
            this.root.setAttribute('aria-hidden', 'true');
            this.root.setAttribute('data-open', 'false');
            this.clearPositionStyles();
        }

        this.isOpen = false;
        this.map = null;
        this.position = null;
        this.closeBtn = null;
        this.contentEl = null;
        this.panelEl = null;
        this.root = null;
        this.onClose = null;
    }

    escapeHtml(value) {
        return String(value ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    render(data = {}) {
        const title = this.escapeHtml(data.title);
        const city = this.escapeHtml(data.city || '');
        const country = this.escapeHtml(data.country || '');
        const location = [city, country].filter(Boolean).join(', ');
        const countryCode = String(data.country_code || '')
            .toLowerCase()
            .replace(/[^a-z]/g, '');
        const flagUrlRaw =
            data.flag_url ||
            (countryCode.length === 2
                ? `https://cdn.jsdelivr.net/npm/flag-icons@7.2.3/flags/1x1/${countryCode}.svg`
                : '');
        const flagUrl = flagUrlRaw ? this.escapeHtml(flagUrlRaw) : '';
        const phone = this.escapeHtml(data.phone || '');
        const email = this.escapeHtml(data.email || '');
        const website = data.website ? this.escapeHtml(data.website) : '';
        const type =
            data.type && typeof data.type === 'object' ? data.type : null;
        const channels = Array.isArray(data.channels) ? data.channels : [];

        const typeColor = type?.color
            ? `style="color:${this.escapeHtml(type.color)}"`
            : '';
        const typeIcon = type?.icon_url
            ? `<img src="${this.escapeHtml(type.icon_url)}" alt="" class="w-16 h-16 object-contain shrink-0" width="16" height="16" />`
            : '';
        const typeHtml = type?.label
            ? `<div class="flex items-center gap-8 text-small font-medium leading-[140%] tracking-[-0.14px] text-system-focused-dark shrink-0" ${typeColor}>${typeIcon}<span>${this.escapeHtml(type.label)}</span></div>`
            : '';

        const flagHtml = flagUrl
            ? `<img src="${flagUrl}" alt="" class="w-[16px] h-[16px] rounded-full object-cover shrink-0" width="20" height="20" loading="lazy" decoding="async" />`
            : '';

        const locationHtml =
            location || flagHtml
                ? `<div class="flex basis-full items-center gap-8 min-w-0 text-small font-normal leading-[140%] tracking-[-0.14px] text-text-primary">${flagHtml}${
                      location
                          ? `<span class="truncate">${location}</span>`
                          : ''
                  }</div>`
                : '';

        const phoneHtml = phone
            ? `<a href="tel:${phone.replace(/\s+/g, '')}" class="inline-flex items-center gap-8 text-small font-medium leading-[140%] tracking-[-0.14px] text-text-primary transition-colors hover:text-text-accent min-w-0"><i class="icon icon-support_agent text-[18px]! shrink-0 text-text-primary" aria-hidden="true"></i><span class="truncate">${phone}</span></a>`
            : '';

        const emailHtml = email
            ? `<a href="mailto:${email}" class="inline-flex items-center gap-8 text-small font-medium leading-[140%] tracking-[-0.14px] text-text-primary transition-colors hover:text-text-accent min-w-0"><i class="icon icon-mail text-[18px]! shrink-0 text-text-primary" aria-hidden="true"></i><span class="truncate">${email}</span></a>`
            : '';

        const contactHtml =
            phoneHtml || emailHtml
                ? `<div class="flex flex-wrap items-center gap-x-16 gap-y-8">${phoneHtml}${emailHtml}</div>`
                : '';

        const useChannelsGrid = channels.length >= 3;
        const channelsHtml = channels.length
            ? `<div class="${
                  useChannelsGrid
                      ? 'grid grid-cols-2 gap-y-8'
                      : 'flex flex-wrap items-center gap-x-16 gap-y-8'
              } rounded-small bg-background-secondary p-8">
                ${channels
                    .map((channel, index) => {
                        const label = this.escapeHtml(channel.label || '');
                        const icon = channel.icon_url
                            ? `<img src="${this.escapeHtml(channel.icon_url)}" alt="" class="w-[16px] h-[16px] object-contain" width="16" height="16" />`
                            : '';
                        const classNames = [
                            'relative flex items-center gap-[6px] text-small font-normal leading-[140%] tracking-[-0.14px] text-text-primary',
                        ];

                        if (useChannelsGrid && index % 2 === 1) {
                            classNames.push(
                                "before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-[24px] before:w-[1.15px] before:bg-border-primary pl-8"
                            );
                        }

                        // Horizontal rule under first row (items 1–2), full grid width.
                        if (useChannelsGrid && channels.length > 2 && index < 2) {
                            classNames.push(
                                "after:pointer-events-none after:absolute after:bottom-[-4px] after:h-[1.15px] after:bg-border-primary after:content-['']"
                            );
                            classNames.push(
                                index === 0
                                    ? 'after:left-0 after:w-[200%]'
                                    : 'after:right-0 after:w-[200%]'
                            );
                        }

                        return `<div class="${classNames.join(' ')}">
                            <span class="flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-full bg-white">${icon}</span>
                            <span>${label}</span>
                        </div>`;
                    })
                    .join('')}
            </div>`
            : '';

        const ctaLabel = this.escapeHtml(
            this.root?.dataset?.ctaLabel || 'Visit partner page'
        );
        const ctaHtml = website
            ? `<a href="${website}" target="_blank" rel="noopener noreferrer" class="button-primary w-full! desktop:w-full! text-center">${ctaLabel}</a>`
            : '';

        return `
            <div class="flex flex-col gap-16 font-primary">
                <div class="flex flex-wrap items-center gap-x-12 gap-y-8 min-w-0">
                    <p class="text-small font-medium leading-[140%] tracking-[-0.14px] text-text-primary shrink-0">${title}</p>
                    ${typeHtml}
                    ${locationHtml}
                </div>
                ${contactHtml ? `${contactHtml}<div class="h-[1.15px] w-full bg-border-primary"></div>` : ''}
                ${channelsHtml}
                ${ctaHtml}
            </div>
        `;
    }
}
